import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Plus, PencilSimple, Trash, Medal } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  winner:        { label: "Winner",        color: "text-secondary border-secondary/40",  icon: Trophy },
  runner_up:     { label: "Runner-up",     color: "text-primary border-primary/40",      icon: Medal },
  semi_finalist: { label: "Semi-finalist", color: "text-chart-3 border-chart-3/40",      icon: Medal },
  highest_break: { label: "Highest Break", color: "text-destructive border-destructive/40", icon: Trophy },
  accolade:      { label: "Accolade",      color: "text-muted-foreground border-border", icon: Medal },
};

/**
 * Legacy achievements card — admin can manually add/edit/delete past
 * tournament results (pre-system era or external events). Display only;
 * zero impact on ELO / rankings / match points.
 */
export default function PlayerLegacyAchievements({ playerId, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/legacy-achievements/player/${playerId}`);
      setItems(r.data?.achievements || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [playerId]);

  useEffect(() => { if (playerId) fetchList(); }, [playerId, fetchList]);

  const onAdd = () => { setEditing({ player_id: playerId, category: "winner", year: new Date().getFullYear() }); setFormOpen(true); };
  const onEdit = (a) => { setEditing(a); setFormOpen(true); };
  const onDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await axios.delete(`${API}/legacy-achievements/${a.id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!items.length && !isAdmin) return null;  // public users see nothing if empty

  return (
    <>
      <Card className="bg-card border-border" data-testid="legacy-achievements-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="w-5 h-5 text-secondary" weight="fill" />
            Past Achievements & Honours
            {items.length > 0 && <Badge variant="outline" className="ml-1">{items.length}</Badge>}
          </CardTitle>
          {isAdmin && (
            <Button size="sm" onClick={onAdd} data-testid="add-legacy-achievement">
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!items.length && !loading && isAdmin && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No historical achievements yet. Click "Add" to record past tournament wins and accolades.
            </p>
          )}
          {items.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {items.map(a => {
                const meta = CATEGORY_META[a.category] || CATEGORY_META.accolade;
                const Icon = meta.icon;
                return (
                  <div key={a.id}
                    className={`p-3 rounded-lg border bg-muted/10 ${meta.color}`}
                    data-testid={`legacy-ach-${a.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className="w-5 h-5 shrink-0 mt-0.5" weight="fill" />
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold text-sm text-foreground truncate" title={a.title}>
                          {a.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {a.tournament_name ? `${a.tournament_name}` : meta.label}
                          {a.year ? ` · ${a.year}` : ""}
                        </p>
                        {a.venue && <p className="text-[10px] text-muted-foreground">{a.venue}</p>}
                        {a.notes && <p className="text-[11px] italic mt-1 line-clamp-2">{a.notes}</p>}
                      </div>
                      {isAdmin && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <button type="button" onClick={() => onEdit(a)}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid={`edit-legacy-${a.id}`}
                            aria-label="Edit">
                            <PencilSimple className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => onDelete(a)}
                            className="text-muted-foreground hover:text-destructive"
                            data-testid={`delete-legacy-${a.id}`}
                            aria-label="Delete">
                            <Trash className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {items.length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-2 text-center italic">
              Historical record — does not affect current ranking points or ELO.
            </p>
          )}
        </CardContent>
      </Card>

      <LegacyAchievementForm
        open={formOpen}
        onOpenChange={setFormOpen}
        achievement={editing}
        onSaved={() => { setEditing(null); setFormOpen(false); fetchList(); }}
      />
    </>
  );
}


// ==================== EDIT / ADD DIALOG ====================
function LegacyAchievementForm({ open, onOpenChange, achievement, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(achievement || {});
  }, [open, achievement]);

  const save = async () => {
    if (!form.title?.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (form.id) {
        await axios.put(`${API}/legacy-achievements/${form.id}`, form, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/legacy-achievements`, form, { withCredentials: true });
        toast.success("Added");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!achievement) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="legacy-achievement-form">
        <DialogHeader>
          <DialogTitle>{form.id ? "Edit Achievement" : "Add Past Achievement"}</DialogTitle>
          <DialogDescription>
            Historical tournament wins, runner-ups, highest breaks, or accolades.
            Records do not affect current ELO or ranking points.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title || ""}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Annadil Burhani Championship"
              data-testid="legacy-title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category || "winner"} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="legacy-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.category === "winner" && (
                <p className="text-[10px] text-secondary mt-1 flex items-center gap-1" data-testid="legacy-winner-hint">
                  <Trophy className="w-3 h-3" weight="fill" />
                  Will count toward the player's "Titles"
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Year</Label>
              <Input type="number" min="1950" max="2100"
                value={form.year || ""}
                onChange={e => setForm({ ...form, year: parseInt(e.target.value) || null })}
                data-testid="legacy-year" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Tournament / event name</Label>
            <Input value={form.tournament_name || ""}
              onChange={e => setForm({ ...form, tournament_name: e.target.value })}
              placeholder="e.g. Spring Cup 2019"
              data-testid="legacy-tournament" />
          </div>

          <div>
            <Label className="text-xs">Venue</Label>
            <Input value={form.venue || ""}
              onChange={e => setForm({ ...form, venue: e.target.value })}
              data-testid="legacy-venue" />
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea value={form.notes || ""}
              onChange={e => setForm({ ...form, notes: e.target.value.slice(0, 500) })}
              rows={2}
              placeholder="e.g. Beat 16 competitors in knockout. Final 5-3 vs …"
              data-testid="legacy-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.title?.trim()} data-testid="save-legacy-achievement">
            {saving ? "Saving…" : form.id ? "Update" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
